import { noop } from 'lodash';
import {
  createContext,
  type MutableRefObject,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import {
  type ImageMinimapDismissReason,
  useDismissImageMinimap,
} from '~/components/AssetModalV2/Visualizer/ImageVisualizer/hooks/useDismissImageMinimap';
import { MAX_ZOOM, MIN_ZOOM } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/shared/constants';
import { isDevOrTestStage } from '~/swr-hooks/utils';

const BUTTON_ZOOM_INCREMENT = 0.25;
const MOUSE_WHEEL_ZOOM_INCREMENT = 0.1;

export type ImageContentControlsContextValue = {
  showOriginal: boolean;
  setShowOriginal: (showOriginal: boolean) => void;
  showSolidBackground: boolean;
  setShowSolidBackground: (showSolidBackground: boolean) => void;
  zoom: number;
  onZoomIn: (posX?: number, posY?: number) => void;
  onZoomOut: (posX?: number, posY?: number) => void;
  updateZoom: (value: number) => void;
  isMinimapAvailable: boolean;
  isMinimapOpen: boolean;
  minimapId: string;
  minimapToggleId: string;
  onDismissMinimap: (reason: ImageMinimapDismissReason) => void;
  onMinimapFocusHandled: () => void;
  onSetMinimapAvailable: (isAvailable: boolean) => void;
  onToggleMinimap: (options?: { focusMinimap?: boolean }) => void;
  shouldFocusMinimap: boolean;
  showOriginalBeforeEditRef: MutableRefObject<boolean>;
};

const showOriginalBeforeEditFallbackRef: MutableRefObject<boolean> = { current: false };

const IMAGE_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE: ImageContentControlsContextValue = {
  showOriginal: false,
  setShowOriginal: noop,
  showOriginalBeforeEditRef: showOriginalBeforeEditFallbackRef,
  showSolidBackground: false,
  setShowSolidBackground: noop,
  zoom: 1,
  onZoomIn: noop,
  onZoomOut: noop,
  updateZoom: noop,
  isMinimapAvailable: false,
  isMinimapOpen: false,
  minimapId: '',
  minimapToggleId: '',
  onDismissMinimap: noop,
  onMinimapFocusHandled: noop,
  onSetMinimapAvailable: noop,
  onToggleMinimap: noop,
  shouldFocusMinimap: false,
};

export const ImageContentControlsContext = createContext<ImageContentControlsContextValue>(
  IMAGE_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE,
);

export type ImageContentControlsContextProviderProps = {
  children: ReactNode;
};

export const ImageContentControlsContextProvider = memo(({ children }: ImageContentControlsContextProviderProps) => {
  const generatedMinimapId = useId();
  const [showOriginal, setShowOriginal] = useState(false);
  const showOriginalBeforeEditRef = useRef<boolean>(false);
  const [showSolidBackground, setShowSolidBackground] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isMinimapAvailable, setIsMinimapAvailable] = useState(false);
  const [isMinimapOpen, setIsMinimapOpen] = useState(false);
  const [shouldFocusMinimap, setShouldFocusMinimap] = useState(false);
  const minimapId = `asset-image-minimap-${generatedMinimapId}`;
  const minimapToggleId = `${minimapId}-toggle`;

  const updateZoom: ImageContentControlsContextValue['updateZoom'] = useCallback((value) => {
    const clamped = Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);
    // Normalize to slider precision (0.1)
    const rounded = Math.round(clamped * 10) / 10;
    setZoom(rounded);
  }, []);

  const onDismissMinimap = useCallback(
    (reason: ImageMinimapDismissReason) => {
      setIsMinimapOpen(false);
      setShouldFocusMinimap(false);

      if (reason === 'escape') {
        requestAnimationFrame(() => {
          document.getElementById(minimapToggleId)?.focus({ preventScroll: true });
        });
      }
    },
    [minimapToggleId],
  );

  const onMinimapFocusHandled = useCallback(() => {
    setShouldFocusMinimap(false);
  }, []);

  const onSetMinimapAvailable = useCallback((isAvailable: boolean) => {
    setIsMinimapAvailable(isAvailable);

    if (!isAvailable) {
      setShouldFocusMinimap(false);
    }
  }, []);

  const onToggleMinimap = useCallback(
    ({ focusMinimap = false }: { focusMinimap?: boolean } = {}) => {
      if (!isMinimapAvailable) {
        return;
      }

      setIsMinimapOpen(!isMinimapOpen);
      setShouldFocusMinimap(isMinimapOpen ? false : focusMinimap);
    },
    [isMinimapAvailable, isMinimapOpen],
  );

  useDismissImageMinimap({ isOpen: isMinimapAvailable && isMinimapOpen, onDismiss: onDismissMinimap });

  const onZoomIn = useCallback((posX?: number, posY?: number) => {
    setZoom((prev) => {
      // Use smaller increment for mouse wheel zooming (when coordinates are provided)
      const increment = posX !== undefined && posY !== undefined ? MOUSE_WHEEL_ZOOM_INCREMENT : BUTTON_ZOOM_INCREMENT;
      const newZoom = prev + increment < MAX_ZOOM ? prev + increment : MAX_ZOOM;

      // Normalize to slider precision (0.1)
      return Math.round(newZoom * 10) / 10;
    });
  }, []);

  const onZoomOut = useCallback(
    (posXOrZoom?: number, posY?: number) => {
      // If called with a single number and no posY, treat it as a zoom level (for keyboard shortcuts)
      if (posXOrZoom !== undefined && posY === undefined && posXOrZoom <= MAX_ZOOM && posXOrZoom >= MIN_ZOOM) {
        updateZoom(posXOrZoom);
        return;
      }

      // Otherwise, treat as zoom out with optional mouse coordinates
      setZoom((prev) => {
        // Use smaller increment for mouse wheel zooming (when coordinates are provided)
        const increment =
          posXOrZoom !== undefined && posY !== undefined ? MOUSE_WHEEL_ZOOM_INCREMENT : BUTTON_ZOOM_INCREMENT;
        const newZoom = prev - increment > MIN_ZOOM ? prev - increment : MIN_ZOOM;

        // Normalize to slider precision (0.1)
        return Math.round(newZoom * 10) / 10;
      });
    },
    [updateZoom],
  );

  const contextValue = useMemo<ImageContentControlsContextValue>(
    () => ({
      isMinimapAvailable,
      isMinimapOpen,
      minimapId,
      minimapToggleId,
      onDismissMinimap,
      onMinimapFocusHandled,
      onSetMinimapAvailable,
      onToggleMinimap,
      onZoomIn,
      onZoomOut,
      setShowOriginal,
      setShowSolidBackground,
      showOriginal,
      showOriginalBeforeEditRef,
      showSolidBackground,
      shouldFocusMinimap,
      updateZoom,
      zoom,
    }),
    [
      isMinimapAvailable,
      isMinimapOpen,
      minimapId,
      minimapToggleId,
      onDismissMinimap,
      onMinimapFocusHandled,
      onSetMinimapAvailable,
      onToggleMinimap,
      onZoomIn,
      onZoomOut,
      showOriginal,
      showSolidBackground,
      shouldFocusMinimap,
      updateZoom,
      zoom,
    ],
  );

  return <ImageContentControlsContext.Provider value={contextValue}>{children}</ImageContentControlsContext.Provider>;
});

ImageContentControlsContextProvider.displayName = 'ImageContentControlsContextProvider';

export const useImageContentControlsContext = (): ImageContentControlsContextValue => {
  const context = useContext(ImageContentControlsContext);

  if (context === IMAGE_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE) {
    const error = new Error(
      'useImageContentControlsContext must be used within an ImageContentControlsContextProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: 'ImageContentControlsContext not found',
      });
    }
  }

  return context;
};
